import { Form, SingleDatePicker } from '@mairistem/composants';

import { useUpdateEffect } from '@mairistem/tools';
import dayjs from 'dayjs';
import React from 'react';
import type { VirementType } from '@/types';
import Montant from './Montant';

interface VirementProps {
  disabled: boolean;
  payeur: string;
  reste: number;
  numero: string;
  dateValidite: string;
  banque: string;
  tireur: string;
  montant: number;
  onChange: (value: VirementType, complet: boolean) => void;
  onError?: (error: string) => void;
}

const Virement = (props: VirementProps) => {
  const {
    disabled,
    payeur,
    reste,
    numero: numeroProps,
    dateValidite: dateProps,
    banque: banqueProps,
    tireur: tireurProps,
    montant: montantProps,
    onChange,
    onError,
  } = props;

  const [numero, setNumero] = React.useState<string>(numeroProps || '');
  const [date, setDate] = React.useState<dayjs.Dayjs>(dateProps ? dayjs(dateProps) : null);
  const [banque, setBanque] = React.useState<string>(banqueProps || '');
  const [tireur, setTireur] = React.useState<string>(tireurProps || '');
  const [montant, setMontant] = React.useState<number>(montantProps || reste);

  const handleTireurKeyDown = React.useCallback(
    (event) => {
      const { key } = event;

      if (key === '+') {
        setTireur(payeur);
        event.preventDefault();
      }
    },
    [payeur],
  );

  const handleChange = (value, setter) => {
    setter(value);
  };

  const handleIconKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        handleChange(payeur, setTireur);
        event.preventDefault();
      }
    },
    [payeur],
  );

  const isComplet = React.useCallback(() => montant > 0 && montant <= reste, [montant, reste]);

  // Effet initial pour notifier le parent si le montant est pré-rempli
  React.useEffect(() => {
    if (!disabled && montant > 0) {
      onChange(
        {
          ...(numero !== '' && { numero }),
          ...(date !== null && { dateValidite: date?.format('DD/MM/YYYY') }),
          ...(banque !== '' && { banque }),
          ...(tireur !== '' && { tireur }),
          ...(montant !== 0 && { montant }),
        },
        isComplet(),
      );
    }
  }, []);

  useUpdateEffect(() => {
    if (!disabled) {
      onChange(
        {
          ...(numero !== '' && { numero }),
          ...(date !== null && { dateValidite: date?.format('DD/MM/YYYY') }),
          ...(banque !== '' && { banque }),
          ...(tireur !== '' && { tireur }),
          ...(montant !== 0 && { montant }),
        },
        isComplet(),
      );
    }
  }, [disabled, numero, date, banque, tireur, montant, isComplet]);

  useUpdateEffect(() => {
    if (montant !== 0 && montant > reste && onError) {
      onError('Le montant ne peut pas être supérieur au reste');
    }
  }, [montant, reste]);

  return (
    <Form.Group style={{ marginTop: 0, marginBottom: 0 }}>
      <Form.Input
        disabled={disabled}
        autoFocus={!disabled}
        value={numero}
        label="N°"
        inline
        placeholder="123456"
        style={{ width: 100 }}
        onChange={(e, { value }) => handleChange(value, setNumero)}
        input={{ 'data-testid': 'referenceVirement' }}
      />
      <Form.Field inline>
        <label>Date</label>
        <SingleDatePicker
          data-testid="dateValiditeVirement"
          disabled={disabled}
          clearable
          value={date}
          onChange={(val: dayjs.Dayjs) => setDate(val)}
          disabledDate={(d) => d.isBefore(dayjs().startOf('day'))}
        />
      </Form.Field>
      <Form.Input
        disabled={disabled}
        value={banque}
        label="Banque"
        inline
        placeholder="Crédit Mutuel"
        onChange={(e, { value }) => handleChange(value, setBanque)}
        input={{ 'data-testid': 'banqueVirement' }}
      />
      <Form.Input
        disabled={disabled}
        value={tireur}
        label="Tireur"
        inline
        placeholder=""
        onChange={(e, { value }) => handleChange(value, setTireur)}
        onKeyDown={handleTireurKeyDown}
        input={{ 'data-testid': 'tireurVirement' }}
        icon={{
          name: 'id card',
          basic: true,
          link: true,
          size: 'large',
          fitted: true,
          style: { width: 40 },
          tabIndex: 0,
          role: 'button',
          'aria-label': 'Reprendre le nom du payeur',
          onClick: () => {
            handleChange(payeur, setTireur);
          },
          onKeyDown: handleIconKeyDown,
          title: 'Reprendre le nom du payeur',
          'data-testid': 'iconPayeurVirement',
        }}
      />
      <Montant
        data-testid="montantVirement"
        disabled={disabled}
        reste={reste}
        montant={montant}
        onChange={setMontant}
      />
    </Form.Group>
  );
};

export default Virement;
